import { DASHBOARD_APP_BY_SLUG, type GlobalAppSlug } from "@/lib/dashboardAppRegistry";

export type AppSettingsPathContext = {
  appSlug: GlobalAppSlug;
  settingsBase: string;
};

export type AppSettingsSidebarItem = {
  name: string;
  href: string;
  tab?: string;
  group?: boolean;
  groupExpandPrefixes?: string[];
  children?: AppSettingsSidebarItem[];
};

export function flattenAppSettingsSidebarItems(
  items: AppSettingsSidebarItem[],
): AppSettingsSidebarItem[] {
  const out: AppSettingsSidebarItem[] = [];
  for (const item of items) {
    if (item.tab) out.push(item);
    if (item.children?.length) out.push(...flattenAppSettingsSidebarItems(item.children));
  }
  return out;
}

export function parseAppSettingsPath(internalPath: string): AppSettingsPathContext | null {
  const m = internalPath.match(/^\/([^/]+)\/settings(?:\/|$)/);
  if (!m) return null;
  const slug = m[1] as GlobalAppSlug;
  if (!(slug in DASHBOARD_APP_BY_SLUG) || slug === "home") return null;
  return { appSlug: slug, settingsBase: `/${slug}/settings` };
}

const CRM_SETTINGS_SIDEBAR: AppSettingsSidebarItem[] = [
  { name: "Settings Home", href: "/crm/settings?view=hub" },
  { name: "Users & Roles", href: "/crm/settings?view=section&tab=roles", tab: "roles" },
  { name: "Permissions", href: "/crm/settings?view=section&tab=permissions", tab: "permissions" },
  { name: "Email", href: "/crm/settings/email" },
  { name: "Lead Forms", href: "/crm/settings?view=section&tab=lead-forms", tab: "lead-forms" },
  { name: "Schedules", href: "/crm/settings?view=section&tab=schedules", tab: "schedules" },
  { name: "Functions", href: "/crm/settings?view=section&tab=functions", tab: "functions" },
  {
    name: "Modules",
    href: "/crm/settings/modules",
    group: true,
    groupExpandPrefixes: ["/crm/settings/modules"],
    children: [
      { name: "Lead", href: "/crm/settings/modules/lead?tab=setup" },
      { name: "Accounts", href: "/crm/settings/modules/accounts" },
      { name: "Contacts", href: "/crm/settings/modules/contacts" },
      { name: "Deals", href: "/crm/settings/modules/deals" },
    ],
  },
  { name: "History", href: "/crm/settings?view=section&tab=history", tab: "history" },
  { name: "Usage", href: "/crm/settings?view=section&tab=usage", tab: "usage" },
];

const FINANCE_TAX_SETUP_HREF = "/finance/settings?view=section&tab=tax-setup";

const FINANCE_SETTINGS_SIDEBAR: AppSettingsSidebarItem[] = [
  { name: "Settings Home", href: "/finance/settings?view=hub" },
  { name: "General", href: "/finance/settings?view=section&tab=general", tab: "general" },
  { name: "Invoices", href: "/finance/settings?view=section&tab=invoices", tab: "invoices" },
  { name: "Templates", href: "/finance/settings?view=section&tab=templates", tab: "templates" },
  {
    name: "Taxes & Compliance",
    href: "/finance/settings/taxes-compliance",
    group: true,
    groupExpandPrefixes: ["/finance/settings"],
    children: [{ name: "Tax Setup", href: FINANCE_TAX_SETUP_HREF, tab: "tax-setup" }],
  },
  { name: "Schedules", href: "/finance/settings?view=section&tab=schedules", tab: "schedules" },
  { name: "Functions", href: "/finance/settings?view=section&tab=functions", tab: "functions" },
  { name: "Custom Modules", href: "/finance/settings?view=section&tab=modules", tab: "modules" },
  { name: "History", href: "/finance/settings?view=section&tab=history", tab: "history" },
  { name: "Usage", href: "/finance/settings?view=section&tab=usage", tab: "usage" },
];

export function getAppSettingsSidebarItems(appSlug: GlobalAppSlug): AppSettingsSidebarItem[] {
  if (appSlug === "crm") return CRM_SETTINGS_SIDEBAR;
  if (appSlug === "finance") return FINANCE_SETTINGS_SIDEBAR;
  return [{ name: "Settings Home", href: `/${appSlug}/settings?view=hub` }];
}

export function appSettingsBackHref(appSlug: GlobalAppSlug): string {
  return DASHBOARD_APP_BY_SLUG[appSlug]?.topNavHref ?? `/${appSlug}`;
}

function parseHrefQuery(href: string): { path: string; params: URLSearchParams } {
  const q = href.indexOf("?");
  if (q === -1) return { path: href, params: new URLSearchParams() };
  return { path: href.slice(0, q), params: new URLSearchParams(href.slice(q + 1)) };
}

export function isAppSettingsSidebarActive(
  internalPath: string,
  searchParams: URLSearchParams,
  item: AppSettingsSidebarItem,
): boolean {
  const { path: hrefPath, params: hrefParams } = parseHrefQuery(item.href);
  const view = (searchParams.get("view") || "").toLowerCase();
  const tab = (searchParams.get("tab") || "").toLowerCase();
  const isCrm = hrefPath.startsWith("/crm/settings");

  if (item.tab === "lead-forms") {
    if (internalPath.startsWith("/crm/settings/lead-forms")) return true;
    if (internalPath === hrefPath && tab === "lead-forms") return true;
    return false;
  }

  if (internalPath.startsWith("/crm/settings/modules/")) {
    if (item.href.startsWith("/crm/settings/modules/lead")) {
      const hrefTab = (hrefParams.get("tab") || "setup").toLowerCase();
      const pathTab = (searchParams.get("tab") || "setup").toLowerCase();
      return internalPath.startsWith("/crm/settings/modules/lead") && pathTab === hrefTab;
    }
    if (hrefPath.startsWith("/crm/settings/modules/")) {
      return internalPath === hrefPath || internalPath.startsWith(`${hrefPath}/`);
    }
  }

  if (hrefPath === "/crm/settings/modules") {
    return internalPath === "/crm/settings/modules";
  }

  if (hrefPath === "/crm/settings/email") {
    return internalPath === "/crm/settings/email" || internalPath.startsWith("/crm/settings/email/");
  }

  if (hrefParams.get("view") === "hub") {
    if (isCrm) {
      if (internalPath.startsWith("/crm/settings/lead-forms")) return false;
      if (internalPath.startsWith("/crm/settings/email")) return false;
    }
    if (internalPath !== hrefPath && !internalPath.startsWith(`${hrefPath}/`)) return false;
    return view === "hub" || (!view && !tab);
  }

  const hrefTab = (hrefParams.get("tab") || "").toLowerCase();
  if (!hrefTab) return internalPath === hrefPath || internalPath.startsWith(`${hrefPath}/`);

  if (isCrm) {
    if (internalPath.startsWith("/crm/settings/lead-forms")) return false;
    if (internalPath.startsWith("/crm/settings/email")) return false;
  }
  if (internalPath !== hrefPath) return false;
  return view !== "hub" && tab === hrefTab;
}
